import { Circle, Heading, TextLink } from '@mm/components';
import { ENTER_KEY_NAME } from '@mm/constants/keys-codes';
import {
  CloseCircleIcon,
  EditIcon,
  PlaceholderIcon,
  TrashIcon,
} from '@mm/icons';
import { useAttributeCRUDActions } from '@mm/state/actions/AttributeCRUDActions';
import { useContactsCRUDActions } from '@mm/state/actions/ContactsCRUDActions';
import { identifyChannel } from '@mm/state/helpers/live-chat-helpers';
import { SelectedContact } from '@mm/state/interfaces/contacts';
import { LiveChatChannels } from '@mm/state/model/LivechatConversationModel';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import atLightIcon from 'assets/icons/at-light.svg';
import clockIcon from 'assets/icons/clock.svg';
import noProfilePicIcon from 'assets/icons/contact_noimage.svg';
import pencilAltIcon from 'assets/icons/pencil-alt.svg';
import smartphoneIcon from 'assets/icons/smartphone.svg';
import userAltIcon from 'assets/icons/user-alt.svg';
import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { ImageWithFallback } from '../../../../../../common/components/ImageWithFallback/ImageWithFallback';
import { colors } from '../../../../beta/styles/colors';
import { ChannelIcon } from '../../../../liveChat/components/ChannelIcon';
import { EmailVerificationStatusIcon } from '../../EmailVerificationStatusIcon/EmailVerificationStatusIcon';
import './contactDetailsHeader.pcss';

type OuterProps = {
  deleteSelectedContact?: () => void;
  closeDetails?: () => void;
  selectedContact: SelectedContact;
  onChangeContactInfo?: () => void;
  isLiveChatContacts?: boolean;
  collapsedContactView?: boolean;
};

type Props = OuterProps;

const ContactsDetailsHeader = (props: Props) => {
  const {
    selectedContact,
    closeDetails,
    deleteSelectedContact,
    onChangeContactInfo,
    isLiveChatContacts,
    collapsedContactView,
  } = props;

  const {
    fullName,
    profilePic,
    lastActive,
    recipientId,
    id: contactId,
    remoteId: remoteUserId,
    email,
    phone = '',
    location,
    unsubscribed,
  } = selectedContact;

  const [newEmailValue, setNewEmailValue] = useState(email || '');
  const [editEmail, setEditEmail] = useState(false);
  const [newPhoneValue, setNewPhoneValue] = useState(phone || '');
  const [editPhone, setEditPhone] = useState(false);
  const [newFullNameValue, setNewFullNameValue] = useState(fullName);
  const [editFullName, setEditFullName] = useState(false);

  const fullNameInputRef = useRef<HTMLInputElement>(null);

  const { addAttribute, deleteAttributeValue } = useAttributeCRUDActions();
  const { update: updateContact } = useContactsCRUDActions();

  const fbPageId = useSelector(activeFacebookPageIdSelector);

  useEffect(() => {
    setEditEmail(false);
    setEditPhone(false);
    setEditFullName(false);
    setNewFullNameValue(fullName);
  }, [fullName, selectedContact.id]);

  const channel = identifyChannel(remoteUserId);

  const allowFullNameEditing = channel !== 'facebook';

  const editField = async (name: string, value: string) => {
    const valueText = value.trim();
    let response;
    if (!valueText) {
      const variable = selectedContact.variables.find(
        v => v.variable.name === name,
      );
      if (variable) {
        response = await deleteAttributeValue(variable.id);
      }
    } else {
      response = await addAttribute(contactId, name, valueText);
    }
    if (response && onChangeContactInfo) {
      onChangeContactInfo();
    }
    setEditEmail(false);
    setEditPhone(false);
  };
  const updateFullName = async () => {
    await updateContact(fbPageId, {
      id: selectedContact.id,
      fullName: newFullNameValue,
    });
    onChangeContactInfo?.();
    setEditFullName(false);
  };

  useEffect(() => {
    setNewEmailValue(email || '');
    setNewPhoneValue(phone || '');
  }, [email, phone]);

  const generateName = (selectedContact: SelectedContact) => {
    if (
      selectedContact.firstName &&
      selectedContact.lastName &&
      selectedContact.username
    ) {
      if (
        selectedContact.firstName === 'Instagram Contact' &&
        selectedContact.lastName === 'IG' &&
        selectedContact.username
      ) {
        return `@${selectedContact.username}`;
      }
      return `${selectedContact.firstName} ${selectedContact.lastName} (@${selectedContact.username})`;
    }
    if (selectedContact.firstName && selectedContact.lastName) {
      return `${selectedContact.firstName} ${selectedContact.lastName}`;
    }
    if (selectedContact.firstName) {
      return `${selectedContact.firstName}`;
    }
    if (selectedContact.lastName) {
      return `${selectedContact.lastName}`;
    }
    if (selectedContact.username) {
      return `@${selectedContact.username}`;
    }
  };

  const emailInputComponent = editEmail ? (
    <p className="contact-details-header__info-item">
      <img
        src={atLightIcon}
        className="contact-details-header__info-icon"
        alt=""
      />
      <input
        className="contact-details-header__input-container"
        value={newEmailValue}
        placeholder="Value"
        onChange={event => setNewEmailValue(event.target.value)}
      />
      <PrimaryButton
        color={ButtonColor.blue}
        className="contact-details-header__info-input-btn"
        onClick={() => editField('EMAIL', newEmailValue)}
      >
        Save
      </PrimaryButton>
      <PrimaryButton
        color={ButtonColor.secondaryBlue}
        className="contact-details-header__info-input-btn"
        onClick={() => {
          setNewEmailValue(email || '');
          setEditEmail(false);
        }}
      >
        Cancel
      </PrimaryButton>
    </p>
  ) : (
    <div className="contact-details-header__info-item">
      <img
        src={atLightIcon}
        className="contact-details-header__info-icon"
        alt=""
      />
      {newEmailValue ? (
        <>
          {newEmailValue}{' '}
          <EmailVerificationStatusIcon
            contact={selectedContact}
            className="contact-details-header__email-verification-icon"
          />
        </>
      ) : (
        <span className="contact-details-header__placeholder">none</span>
      )}
      <img
        src={pencilAltIcon}
        className="contact-details-header__info-edit-icon"
        alt=""
        onClick={() => setEditEmail(true)}
      />
    </div>
  );

  const phoneInputComponent = editPhone ? (
    <p className="contact-details-header__info-item">
      <img
        src={smartphoneIcon}
        className="contact-details-header__info-icon"
        alt=""
      />
      <input
        className="contact-details-header__input-container"
        value={newPhoneValue}
        placeholder="Value"
        onChange={event => setNewPhoneValue(event.target.value)}
      />
      <PrimaryButton
        color={ButtonColor.blue}
        className="contact-details-header__info-input-btn"
        onClick={() => editField('PHONE', newPhoneValue)}
      >
        Save
      </PrimaryButton>
      <PrimaryButton
        color={ButtonColor.secondaryBlue}
        className="contact-details-header__info-input-btn"
        onClick={() => {
          setNewPhoneValue(phone || '');
          setEditPhone(false);
        }}
      >
        Cancel
      </PrimaryButton>
    </p>
  ) : (
    <p className="contact-details-header__info-item">
      <img
        src={smartphoneIcon}
        className="contact-details-header__info-icon"
        alt=""
      />
      {newPhoneValue ? (
        newPhoneValue
      ) : (
        <span className="contact-details-header__placeholder">none</span>
      )}
      <img
        src={pencilAltIcon}
        className="contact-details-header__info-edit-icon"
        alt=""
        onClick={() => setEditPhone(true)}
      />
    </p>
  );

  const fullNameInputComponent = editFullName ? (
    <>
      <input
        ref={fullNameInputRef}
        className="contact-details-header__profile-name-input"
        value={newFullNameValue}
        placeholder="Value"
        onChange={event => setNewFullNameValue(event.target.value)}
        onKeyDown={event => {
          if (event.key === ENTER_KEY_NAME) {
            updateFullName();
          }
        }}
      />
      <div className="contact-details-header__profile-name-buttons">
        <PrimaryButton
          color={ButtonColor.secondaryBlue}
          className="contact-details-header__profile-name-input-button"
          disabled={newFullNameValue.trim().length === 0}
          onClick={updateFullName}
        >
          Edit
        </PrimaryButton>
        <PrimaryButton
          color={ButtonColor.invertedRed}
          className="contact-details-header__profile-name-input-button"
          onClick={() => {
            setNewFullNameValue(fullName);
            setEditFullName(false);
          }}
        >
          Cancel
        </PrimaryButton>
      </div>
    </>
  ) : (
    <>
      <Heading
        size={3}
        text={generateName(selectedContact)}
        className="contact-details-header__profile-name"
      />
    </>
  );

  const userInfoComponent = (
    <div className="contact-details-header__user-info">
      {lastActive && (
        <p className="contact-details-header__info-item">
          <img
            src={clockIcon}
            className="contact-details-header__info-icon"
            alt=""
          />
          Last seen: {moment(lastActive).format('YYYY-MM-DD HH:mm')}
        </p>
      )}
      {recipientId && (
        <p className="contact-details-header__info-item">
          <img
            src={userAltIcon}
            className="contact-details-header__info-icon"
            alt=""
          />
          {recipientId}
        </p>
      )}
      {emailInputComponent}
      {phoneInputComponent}
      {location && (
        <p className="contact-details-header__info-item">
          <PlaceholderIcon className="contact-details-header__info-icon" />
          {location}
        </p>
      )}
    </div>
  );

  const profilePicture = (
    <ImageWithFallback
      src={profilePic}
      fallbackImage={noProfilePicIcon}
      className="contact-details-header__profile-pic-image"
      alt={fullName}
    />
  );

  if (collapsedContactView) {
    return (
      <div className="contact-details-header--collapsed">
        <div className="contact-details-header__profile-pic-container">
          {profilePicture}
        </div>
      </div>
    );
  }

  const showSubscribedIndicator =
    !isLiveChatContacts && unsubscribed !== undefined;

  return (
    <div className="contact-details-header">
      <div className="contact-details-header__topbar">
        <div className="contact-details-header__profile-pic">
          {profilePicture}
        </div>

        <div>
          <div className="contact-details-header__profile-name-container">
            {fullNameInputComponent}
          </div>

          {showSubscribedIndicator && (
            <div className="contact-details-header__profile-subscribed-indicator">
              <Circle
                className="contact-details-header__profile-subscribed-indicator-badge"
                color={unsubscribed ? colors.grayLight : colors.green}
              />
              <TextLink className="contact-details-header__profile-subscribed-indicator-text">
                {unsubscribed ? 'Unsubscribed' : 'Subscribed'}
              </TextLink>
            </div>
          )}
        </div>
        <div className="contact-details-header__action-buttons">
          {allowFullNameEditing && !editFullName && (
            <EditIcon
              color="white"
              className="contact-details-header__edit-icon contact-details-header__action-btn"
              onClick={() => {
                setEditFullName(true);
                setTimeout(() => fullNameInputRef.current?.focus(), 50);
              }}
            />
          )}
          {deleteSelectedContact && (
            <TrashIcon
              className="contact-details-header__action-btn"
              onClick={deleteSelectedContact}
              color="white"
            />
          )}
          {closeDetails && (
            <CloseCircleIcon
              className="contact-details-header__action-btn"
              onClick={closeDetails}
              color="white"
            />
          )}
        </div>
      </div>

      {!selectedContact.loading && (
        <div className="contact-details-header__profile-info">
          <div className="contact-details-header_profile-info-block">
            <p className="contact-details-header__contact-channel-icon-container">
              <span className="contact-details-header__info-icon">
                <ChannelIcon remoteUserId={remoteUserId} />
              </span>
              <span className="contact-details-header__contact-channel-name">
                {LiveChatChannels[channel]}
              </span>
            </p>

            {userInfoComponent}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactsDetailsHeader;
